import calibratePhoto from '../../elements/calibrate-photo/calibrate-photo.js';
import { ParseHeight } from '../FormatHeight.js';
import { markerIsOffPhotoHelper } from './PhotoWarnings.js';

/**
 * @typedef {object} StickAlignmentPoint
 * @property {number} score - The score of the point
 * @property {number} height - The height of the point
 */

/**
 * @typedef {object} StickAlignmentData
 * @property {boolean} stickAlignIsPossible - Whether or not the photo can have a stick alignment
 * @property {object} validationDetails - An object containing the validation details for the stick alignment
 * @property {boolean} validationDetails.photoHasSufficientNumberOfAnchors - Whether or not the photo has a sufficient number of anchor
 * points for stick alignment
 * @property {boolean} validationDetails.cameraTypeIsValid - Whether or not the camera type is valid
 * @property {boolean} validationDetails.allAnchorHeightsAreValid - Whether or not all the anchor heights are valid
 * @property {boolean} validationDetails.fullAlignResultIsValid - Whether or not the full align result is valid
 * @property {StickAlignmentPoint[]?} stickAlignPoints - An array of stick alignment points (or null if stick alignment is not possible)
 * @property {object?} stickAlignData - An object containing the stick alignment data (or null if stick alignment is not possible)
 */

/**
 * Returns the stick alignment validation details and data for a photo.
 * @param {object} photo - A photo object from a job
 * @param {object} [options] - An object containing options for the function
 * @param {boolean} [options.testCameraModel] - A camera model to test the photo against. If provided, this camera model will be used
 * instead of the camera model from the photo, and the `cameraTypeIsValid` validation check will automatically pass.
 * @param {object} [options.confirmedFlatCameras] - A list of camera models that are confirmed to be flat. If provided and the photo's
 * camera information is found in this lookup, the `cameraTypeIsValid` validation check will automatically pass.
 * @returns {StickAlignmentData} An object containing the stick alignment data and validation details
 */
export function getStickAlignmentData(photo, options = {}) {
  const { testCameraModel, confirmedFlatCameras } = options;

  const anchorCalibrationMarkerEntries = Object.entries(photo.photofirst_data?.anchor_calibration ?? {});

  // Check if we have enough anchor points to perform stick alignment
  const numberOfAnchors = anchorCalibrationMarkerEntries.length;
  const photoHasSufficientNumberOfAnchors = numberOfAnchors > 3;

  // Check if the camera type is valid
  const cameraType = getCameraType(photo, testCameraModel, confirmedFlatCameras);
  const cameraTypeIsValid = cameraType !== null;

  // Compute the stick alignment data
  const {
    allAnchorHeightsAreValid = false,
    fullAlignResultIsValid = false,
    stickAlignPoints = null,
    stickAlignData = null
  } = photoHasSufficientNumberOfAnchors && cameraTypeIsValid ? computeStickAlignment(photo, cameraType, testCameraModel) : {};

  // Return the stick alignment data along with all the validation checks
  const validationDetails = {
    photoHasSufficientNumberOfAnchors,
    cameraTypeIsValid,
    allAnchorHeightsAreValid,
    fullAlignResultIsValid
  };
  const stickAlignIsPossible = Object.values(validationDetails).every((check) => check == true);
  return { stickAlignIsPossible, validationDetails, stickAlignPoints, stickAlignData };
}

/**
 * Get the camera type for a photo (or an overridden type from less common sources).
 * @param {object} photo - A photo object from a job
 * @param {object} [testCameraModel] - A camera model to test the photo against.
 * @param {object} [confirmedFlatCameras] - A lookup of camera models that are confirmed to be flat.
 * @returns {string|null} The camera type to use for alignment calculations or null if the camera type is invalid.
 */
function getCameraType(photo, testCameraModel, confirmedFlatCameras) {
  const cameraFoundInConfirmedFlatCameras = confirmedFlatCameras != null && findCameraInConfirmedFlatCameras(photo, confirmedFlatCameras);
  const cameraTypeFromPhoto = calibratePhoto.getMatchingCameraModel(photo)?.type;
  const cameraType = testCameraModel != null ? 'new' : cameraTypeFromPhoto;
  return cameraType ?? (cameraFoundInConfirmedFlatCameras || photo.inherited_photo_id !== null ? 'flat' : null);
}

/**
 * Determines if a camera is flat based on the camera model and the confirmed flat cameras lookup.
 * @param {object} photo - A photo object from a job
 * @param {object} confirmedFlatCameras - A lookup of camera models that are confirmed to be flat.
 * @returns {boolean} Whether or not the camera is flat.
 */
function findCameraInConfirmedFlatCameras(photo, confirmedFlatCameras) {
  const flatCameraKey = [photo.camera_model, photo.lens, photo.focal_length, photo.image_width, photo.image_height].join('');
  return confirmedFlatCameras[flatCameraKey] != null;
}

/**
 * Computes the stick alignment data for a photo.
 * @param {object} photo - A photo object from a job
 * @param {string} cameraType - The camera type to use for alignment calculations
 * @param {object} testCameraModel - A camera model to test the photo against
 * @returns {{
 *   allAnchorHeightsAreValid: boolean,
 *   fullAlignResultIsValid: boolean,
 *   stickAlignPoints?: StickAlignmentPoint[],
 *   stickAlignData?: object
 * }} An object containing the stick alignment data and validation details
 */
function computeStickAlignment(photo, cameraType, testCameraModel) {
  // Normalize the image orientation
  const { image_width: imageWidth, image_height: imageHeight, rotation } = photo;
  const imageIsRotated = rotation == 1 || rotation == 3;
  const [width, height] = imageIsRotated ? [imageHeight, imageWidth] : [imageWidth, imageHeight];

  // Transform the anchor calibration data into alignment values
  const anchorCalibrationMarkerEntries = Object.entries(photo.photofirst_data?.anchor_calibration ?? {});
  const alignmentValues = anchorCalibrationMarkerEntries.map(([, marker]) => ({
    height: ParseHeight(String(marker.height)).decimalFeet,
    pixel_selection: marker.pixel_selection[0],
    x: (marker.pixel_selection[0].percentX * width) / 100,
    y: (marker.pixel_selection[0].percentY * height) / 100
  }));
  const allAnchorHeightsAreValid = alignmentValues.every(
    ({ height, pixel_selection }) => !isNaN(height) && !markerIsOffPhotoHelper(pixel_selection)
  );

  // Remove pixel_selection because we only needed it for validation
  for (const value of alignmentValues) delete value.pixel_selection;

  // Return early if any anchor heights are invalid
  if (!allAnchorHeightsAreValid) return { allAnchorHeightsAreValid, fullAlignResultIsValid: false };

  // Perform the full alignment calculation
  const fullAlignResult =
    /** @type {ReturnType<typeof calibratePhoto.fullAlign> & { points: StickAlignmentPoint[] }} */
    (calibratePhoto.fullAlign(alignmentValues, width, height, photo.focal_length, cameraType, testCameraModel));
  const fullAlignResultIsValid = !isNaN(fullAlignResult.A);

  // Return early if the full alignment result is invalid
  if (!fullAlignResultIsValid) return { allAnchorHeightsAreValid, fullAlignResultIsValid };

  // Extract the points from the stick alignment data
  const { points: stickAlignPoints, ...stickAlignData } = fullAlignResult;

  // Return the stick alignment data along with the validation checks
  return { allAnchorHeightsAreValid, fullAlignResultIsValid, stickAlignPoints, stickAlignData };
}
